import React, { useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { SurveysAPI } from '../../API';

// Universal returns C1 — reconciliation upload. The client's file of
// respondent ids + outcomes for this survey (xlsx/csv). Preview classifies
// every row against this survey family (matched / already final / not found
// / conflicting / invalid); Apply, after a confirm, records the matched
// Pending rows through the same backend core as a live callback (rewards,
// partner quota, partner webhooks). Final statuses are never overwritten and
// applying the same file twice records nothing new.

type Category = 'matched' | 'already_final' | 'not_found' | 'conflicting' | 'invalid';
type PreviewRow = {
    row: number; id: string; rawStatus: string; status: string | null; category: Category; reason: string | null;
    source: string | null; market: string | null; storedStatus: string | null; willRefuse: string | null;
    outcome?: string; finalStatus?: string | null; outcomeReason?: string | null;
};
type Preview = { survey: { id: string; uniqueid: string; name: string }; counts: Record<string, number>; rows: PreviewRow[]; errors: { marketCode: string; message: string }[] };

const CATEGORIES: { key: Category; label: string; variant: string }[] = [
    { key: 'matched', label: 'Matched', variant: 'success' },
    { key: 'already_final', label: 'Already final', variant: 'secondary' },
    { key: 'not_found', label: 'Not found', variant: 'warning' },
    { key: 'conflicting', label: 'Conflicting', variant: 'danger' },
    { key: 'invalid', label: 'Invalid', variant: 'dark' },
];

const APPLY_CHUNK = 200;
const SHOW_ROWS = 500;

const ID_GUESS = /^(uid|user ?id|userid|respondent ?id|resp ?id|panelist ?id|rid|id|ids|pid|psid|transaction ?id|trans_id)$/i;
const STATUS_GUESS = /^(status|outcome|disposition|result|final ?status|code)$/i;

// Cells to strings; Excel numbers stay as written ("1", not "1.0").
const cell = (v: any): string => (v === null || v === undefined ? '' : String(v).trim());

export function guessColumns(headers: string[]): { idCol: number; statusCol: number } {
    const idCol = headers.findIndex((h) => ID_GUESS.test(cell(h)));
    const statusCol = headers.findIndex((h) => STATUS_GUESS.test(cell(h)));
    return { idCol: idCol === -1 ? 0 : idCol, statusCol: statusCol === -1 ? Math.min(1, headers.length - 1) : statusCol };
}

// The sheet as rows of cells (first sheet; first row = headers).
export function readSheet(data: ArrayBuffer): string[][] {
    const wb = XLSX.read(new Uint8Array(data), { type: 'array', raw: false });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: false });
    return rows.map((r) => r.map(cell));
}

export const ReconciliationUpload: React.FC<{ surveyId: string; show: boolean; onClose: () => void }> = ({ surveyId, show, onClose }) => {
    const [fileName, setFileName] = useState('');
    const [sheet, setSheet] = useState<string[][]>([]);
    const [idCol, setIdCol] = useState(0);
    const [statusCol, setStatusCol] = useState(1);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [filter, setFilter] = useState<Category | 'all'>('all');
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ recorded: number; refused: number; rows: PreviewRow[] } | null>(null);

    const reset = () => {
        setFileName(''); setSheet([]); setPreview(null); setResult(null); setError(null); setFilter('all');
    };
    const close = () => { reset(); onClose(); };

    const headers = sheet[0] || [];
    const dataRows = () => sheet.slice(1)
        .map((r, i) => ({ row: i + 2, id: cell(r[idCol]), status: cell(r[statusCol]) }))
        .filter((r) => r.id || r.status);

    const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files && e.target.files[0];
        reset();
        if (!f) return;
        setFileName(f.name);
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const rows = readSheet(reader.result as ArrayBuffer);
                if (rows.length < 2) { setError('The file has no data rows under the header row.'); return; }
                const g = guessColumns(rows[0]);
                setSheet(rows); setIdCol(g.idCol); setStatusCol(g.statusCol);
            } catch (err: any) {
                setError(`Could not read the file: ${err?.message || err}`);
            }
        };
        reader.readAsArrayBuffer(f);
    };

    const runPreview = () => {
        setBusy('Checking every row…'); setError(null); setResult(null);
        SurveysAPI.reconcilePreview({ surveyId, rows: dataRows() })
            .then((p: Preview) => { setPreview(p); setFilter('all'); })
            .catch((err: any) => setError(err?.response?.data?.message || err?.message || 'Preview failed'))
            .finally(() => setBusy(null));
    };

    const runApply = async () => {
        if (!preview) return;
        const matched = preview.rows.filter((r) => r.category === 'matched');
        if (matched.length === 0) return;
        const confirm = await Swal.fire({
            icon: 'warning',
            title: `Record ${matched.length} outcome${matched.length === 1 ? '' : 's'}?`,
            html: `Only Pending respondents of this survey are recorded, the same way a live end-link hit is (rewards, partner quota, partner webhooks). Final statuses are not changed.<br/><br/>File: <strong>${fileName.replace(/</g, '&lt;')}</strong>`,
            showCancelButton: true,
            confirmButtonText: 'Apply',
        });
        if (!confirm.isConfirmed) return;
        setError(null);
        let uploadId: string | undefined;
        let recorded = 0;
        let refused = 0;
        const done: PreviewRow[] = [];
        try {
            for (let i = 0; i < matched.length; i += APPLY_CHUNK) {
                setBusy(`Recording ${Math.min(i + APPLY_CHUNK, matched.length)} of ${matched.length}…`);
                const chunk = matched.slice(i, i + APPLY_CHUNK).map((r) => ({ row: r.row, id: r.id, status: r.rawStatus }));
                const res: any = await SurveysAPI.reconcileApply({
                    surveyId, fileName, rows: chunk, uploadId, fileRows: preview.counts.total, fileCounts: preview.counts,
                });
                uploadId = res.uploadId;
                recorded += res.applied.recorded;
                refused += res.applied.refused;
                done.push(...res.rows);
            }
            setResult({ recorded, refused, rows: done });
        } catch (err: any) {
            setError(`Stopped after ${recorded + refused} rows: ${err?.response?.data?.message || err?.message || 'apply failed'}. Applying the same file again is safe (recorded rows are skipped).`);
            setResult({ recorded, refused, rows: done });
        } finally {
            setBusy(null);
        }
    };

    const shown = preview ? preview.rows.filter((r) => filter === 'all' || r.category === filter) : [];
    const refusedRows = result ? result.rows.filter((r) => r.outcome === 'refused') : [];
    const willRefuse = preview ? preview.rows.filter((r) => r.category === 'matched' && r.willRefuse).length : 0;

    return (
        <Modal show={show} onHide={close} size="xl" centered backdrop="static" style={{ zIndex: 1201 }}>
            <Modal.Header closeButton>
                <Modal.Title>Reconcile outcomes from a client file</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '78vh', overflow: 'auto' }}>
                <p className="text-muted mb-2" style={{ fontSize: '0.9em' }}>
                    An .xlsx or .csv with a respondent ID column (our ID, or the client's own ID when it is the partner's RID on this survey)
                    and an outcome column (complete / terminate / overquota / quality, or 1 / 2 / 3 / 4). Covers every market of this survey.
                </p>
                <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} disabled={!!busy} />
                {headers.length > 0 && (
                    <div className="d-flex flex-wrap align-items-end mt-3" style={{ gap: 12 }}>
                        <label className="mb-0">ID column
                            <select className="form-control form-control-sm" value={idCol} onChange={(e) => { setIdCol(Number(e.target.value)); setPreview(null); }}>
                                {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                            </select>
                        </label>
                        <label className="mb-0">Outcome column
                            <select className="form-control form-control-sm" value={statusCol} onChange={(e) => { setStatusCol(Number(e.target.value)); setPreview(null); }}>
                                {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                            </select>
                        </label>
                        <span className="text-muted" style={{ fontSize: '0.9em' }}>{sheet.length - 1} data rows</span>
                        <Button size="sm" onClick={runPreview} disabled={!!busy}>Preview</Button>
                    </div>
                )}
                {busy && <div className="mt-3"><Spinner animation="border" size="sm" className="mr-2" />{busy}</div>}
                <Alert variant="danger" show={!!error} className="mt-3">{error}</Alert>

                {preview && (
                    <div className="mt-3">
                        {preview.errors.length > 0 && (
                            <Alert variant="warning">
                                Could not search {preview.errors.map((e) => e.marketCode).join(', ')}: panel respondents there show as not found. Try again later.
                            </Alert>
                        )}
                        <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
                            <Button size="sm" variant={filter === 'all' ? 'primary' : 'outline-primary'} onClick={() => setFilter('all')}>All {preview.counts.total}</Button>
                            {CATEGORIES.map((c) => (
                                <Button key={c.key} size="sm" variant={filter === c.key ? c.variant : `outline-${c.variant}`} onClick={() => setFilter(c.key)}>
                                    {c.label} {preview.counts[c.key] || 0}
                                </Button>
                            ))}
                        </div>
                        {willRefuse > 0 && (
                            <Alert variant="warning" className="py-2">
                                {willRefuse} matched row{willRefuse === 1 ? ' is' : 's are'} on a survey that is expired or not open: the live rules will refuse {willRefuse === 1 ? 'it' : 'them'}.
                            </Alert>
                        )}
                        <Table size="sm" bordered hover>
                            <thead><tr><th>Row</th><th>ID</th><th>Outcome in file</th><th>Result</th><th>Detail</th></tr></thead>
                            <tbody>
                                {shown.slice(0, SHOW_ROWS).map((r) => {
                                    const cat = CATEGORIES.find((c) => c.key === r.category)!;
                                    return (
                                        <tr key={r.row}>
                                            <td>{r.row}</td>
                                            <td style={{ wordBreak: 'break-all' }}>{r.id}</td>
                                            <td>{r.rawStatus}{r.status && r.status !== r.rawStatus ? <span className="text-muted"> → {r.status}</span> : null}</td>
                                            <td><Badge variant={cat.variant}>{cat.label}</Badge></td>
                                            <td style={{ fontSize: '0.85em' }}>
                                                {r.reason || (r.source ? `${r.source}${r.market ? ` (${r.market})` : ''}` : '')}
                                                {r.willRefuse ? <span className="text-danger"> · will be refused: {r.willRefuse}</span> : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        {shown.length > SHOW_ROWS && <div className="text-muted">Showing the first {SHOW_ROWS} of {shown.length}.</div>}
                    </div>
                )}

                {result && (
                    <Alert variant={result.refused ? 'warning' : 'success'} className="mt-3">
                        Recorded {result.recorded}. {result.refused ? `Refused by the live rules: ${result.refused}.` : ''}
                        {refusedRows.length > 0 && (
                            <ul className="mb-0 mt-1">
                                {refusedRows.slice(0, 50).map((r) => <li key={r.row}>Row {r.row} ({r.id}): {r.outcomeReason || 'not recorded'}</li>)}
                            </ul>
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={close} disabled={!!busy}>Close</Button>
                {preview && !result && (
                    <Button variant="success" onClick={runApply} disabled={!!busy || !preview.counts.matched}>
                        Apply {preview.counts.matched || 0} matched
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};
